import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function ActionMenu({ label = "Actions", items = [], align = "right", verticalAlign = "down" }) {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  // Detect if we're inside a compact-actions-row (dark theme variant)
  useEffect(() => {
    if (wrapperRef.current?.closest(".compact-actions-row")) {
      setIsDark(true);
    }
  }, []);

  const handleToggle = () => {
    if (!open && wrapperRef.current) {
      setTriggerRect(wrapperRef.current.getBoundingClientRect());
    }
    setOpen(v => !v);
  };

  // Close on outside mousedown or Escape
  useEffect(() => {
    if (!open) return undefined;
    const handleClose = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handleClose);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  // Recompute position on scroll / resize so the popover tracks the trigger
  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (wrapperRef.current) setTriggerRect(wrapperRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const visibleItems = items.filter(Boolean);
  if (visibleItems.length === 0) return null;

  const menuWidth = isDark ? 200 : 170;
  const viewportPadding = 8;
  const estimatedMenuHeight = visibleItems.length * 44 + 16;
  const shouldOpenUp = triggerRect
    ? (
        verticalAlign === "up" ||
        (verticalAlign !== "up" &&
          triggerRect.bottom + 8 + estimatedMenuHeight > window.innerHeight - viewportPadding &&
          triggerRect.top - 8 > window.innerHeight - triggerRect.bottom)
      )
    : verticalAlign === "up";
  const computedLeft = triggerRect
    ? Math.min(
        window.innerWidth - menuWidth - viewportPadding,
        Math.max(
          viewportPadding,
          align === "left" ? triggerRect.left : triggerRect.right - menuWidth
        )
      )
    : viewportPadding;
  const computedTop = triggerRect
    ? (shouldOpenUp
        ? Math.max(viewportPadding, triggerRect.top - estimatedMenuHeight - 8)
        : Math.min(window.innerHeight - estimatedMenuHeight - viewportPadding, triggerRect.bottom + 8))
    : viewportPadding;

  // Build fixed-position style so the popover sits above ALL sibling cards
  const popoverStyle = triggerRect ? {
    position: "fixed",
    zIndex: 1000,
    width: menuWidth,
    minWidth: menuWidth,
    maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
    overflowY: "auto",
    top: shouldOpenUp ? "auto" : computedTop,
    bottom: shouldOpenUp ? Math.max(viewportPadding, window.innerHeight - triggerRect.top + 8) : "auto",
    left: computedLeft,
    right: "auto",
    // Dark variant styles (replaces .compact-actions-row .action-menu-popover CSS)
    ...(isDark ? {
      background: "rgba(22, 19, 17, 0.98)",
      borderColor: "rgba(255, 255, 255, 0.08)",
      boxShadow: "0 12px 28px rgba(0,0,0,0.32)",
    } : {}),
  } : {};

  return (
    <div className="action-menu" ref={wrapperRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="btn btn-secondary btn-sm action-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleToggle}
      >
        {label}
        <span style={{ fontSize: 11, color: "var(--ink-hint)" }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && triggerRect && createPortal(
        <div
          ref={popoverRef}
          className="action-menu-popover"
          role="menu"
          style={popoverStyle}
        >
          {visibleItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`action-menu-item${item.danger ? " is-danger" : ""}${isDark ? " action-menu-item-dark" : ""}`}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onClick?.();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
